import { z } from 'zod';
import { AppointmentStatusSchema, CallStatusSchema, ChannelKindSchema, PermissionStatusSchema, RoutineStateSchema, WebhookEventTypeSchema } from './enums.js';
import { HhmmSchema, WeeklyHoursSchema } from './schedule.js';
import { RecordedAppointmentOutcomeSchema } from './cadence.js';
import { FunnelStageInputSchema } from './api-funnel.js';

// Automations: routine definitions, templates, enrollments, step logs and dry runs (PRD §13).
// A routine definition is versioned JSON stored in RoutineVersion.definition and validated with these schemas.

const Id = z.string().min(1).max(64);
const Iso = z.string();
const StepId = z.string().min(1).max(40);

// ───────────── Triggers ─────────────

export const ROUTINE_TRIGGER_TYPES = [
  'lead_created',
  'lead_stage_changed',
  'whatsapp_inbound',
  'call_outcome',
  'appointment_changed',
  'appointment_outcome',
  'no_reply',
  'manual',
  'external_event',
] as const;
export type RoutineTriggerType = (typeof ROUTINE_TRIGGER_TYPES)[number];

export const RoutineTriggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('lead_created') }),
  z.object({ type: z.literal('lead_stage_changed'), stage: FunnelStageInputSchema }),
  z.object({ type: z.literal('whatsapp_inbound') }),
  z.object({ type: z.literal('call_outcome'), outcomes: z.array(CallStatusSchema).min(1).max(10) }),
  z.object({ type: z.literal('appointment_changed'), statuses: z.array(AppointmentStatusSchema).min(1).max(10) }),
  /** Advisor/CRM-reported outcome (event `appointment.outcome_recorded`, spec §03.2, §10.5). */
  z.object({ type: z.literal('appointment_outcome'), outcomes: z.array(RecordedAppointmentOutcomeSchema).min(1).max(4) }),
  z.object({ type: z.literal('no_reply'), hours: z.number().int().min(1).max(24 * 60) }),
  z.object({ type: z.literal('manual') }),
  z.object({ type: z.literal('external_event'), eventType: z.string().min(1).max(80) }),
]);
export type RoutineTrigger = z.infer<typeof RoutineTriggerSchema>;

// ───────────── Eligibility conditions ─────────────

export const RoutineConditionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('project_in'), projectIds: z.array(Id).min(1).max(50) }),
  z.object({ type: z.literal('stage_in'), stages: z.array(FunnelStageInputSchema).min(1).max(20) }),
  z.object({ type: z.literal('source_system_equals'), value: z.string().min(1).max(60) }),
  z.object({ type: z.literal('permission_granted'), channel: ChannelKindSchema }),
  z.object({ type: z.literal('not_test_contact') }),
]);
export type RoutineCondition = z.infer<typeof RoutineConditionSchema>;

// ───────────── Steps ─────────────

export const WAIT_UNITS = ['minutes', 'hours', 'days'] as const;
export const BRANCH_CONDITIONS = ['answered', 'replied', 'booked', 'eligible'] as const;
export type BranchCondition = (typeof BRANCH_CONDITIONS)[number];

/** Outreach steps default to being cancelled when the lead replies on any channel (silence-based, PRD §13). */
const cancelIfReplied = z.boolean().default(true);
/**
 * Cadence day guard (spec §03.1 "segunda llamada más tarde el mismo día"): when the step could only run after this
 * cadence day (contact window, frequency or call lock pushed it), it is skipped with a reason instead of piling up
 * on a later day. Day 1 = the cadence start day in the project's timezone.
 */
const maxDay = z.number().int().min(1).max(30).optional();

export const RoutineActionStepSchema = z.discriminatedUnion('type', [
  z.object({
    id: StepId,
    type: z.literal('whatsapp_template'),
    templateName: z.string().min(1).max(120),
    language: z.string().min(2).max(10).default('es_MX'),
    variables: z.record(z.string().max(40), z.string().max(300)).default({}),
    cancelIfReplied,
    maxDay,
  }),
  z.object({ id: StepId, type: z.literal('whatsapp_text'), text: z.string().min(1).max(1000), cancelIfReplied, maxDay }),
  z.object({ id: StepId, type: z.literal('whatsapp_media'), assetId: Id, caption: z.string().max(1000).optional(), cancelIfReplied, maxDay }),
  z.object({ id: StepId, type: z.literal('ai_call'), cancelIfReplied, maxDay }),
  z.object({ id: StepId, type: z.literal('wait'), amount: z.number().int().min(1).max(10_000), unit: z.enum(WAIT_UNITS) }),
  /**
   * Day-anchored wait (spec §03.1/§15.3 "Día 2 … Día 7"): until day N of the cadence at HH:MM in the project's timezone
   * (no wait when that moment already passed). Outreach after it is still placed inside the contact window.
   */
  z.object({ id: StepId, type: z.literal('wait_until_day'), day: z.number().int().min(1).max(30), at: HhmmSchema.default('10:00') }),
  /**
   * A routine may only close (Perdido, with a catalogue reason — e.g. cadence exhausted → No contactado, §03.1c) or
   * park (Potencial futuro, with a condition). Every other stage moves only on real events (§05.1); the engine skips
   * any other target with a reason.
   */
  z.object({
    id: StepId,
    type: z.literal('update_stage'),
    stage: FunnelStageInputSchema,
    lossReason: z.string().min(1).max(64).optional(),
    note: z.string().max(500).optional(),
    followUpCondition: z.string().max(500).optional(),
  }),
  z.object({ id: StepId, type: z.literal('notify_advisor'), message: z.string().min(1).max(500) }),
  z.object({ id: StepId, type: z.literal('create_task'), title: z.string().min(1).max(200) }),
  z.object({ id: StepId, type: z.literal('send_webhook'), eventType: WebhookEventTypeSchema }),
]);
export type RoutineActionStep = z.infer<typeof RoutineActionStepSchema>;
export type RoutineActionType = RoutineActionStep['type'];

/** Limited branch (PRD §13): one level only — arms contain actions, never nested branches. */
export const RoutineBranchStepSchema = z.object({
  id: StepId,
  type: z.literal('branch'),
  condition: z.enum(BRANCH_CONDITIONS),
  /** Only for condition = eligible. */
  conditions: z.array(RoutineConditionSchema).max(10).default([]),
  ifTrue: z.array(RoutineActionStepSchema).max(20).default([]),
  ifFalse: z.array(RoutineActionStepSchema).max(20).default([]),
});
export type RoutineBranchStep = z.infer<typeof RoutineBranchStepSchema>;

export const RoutineStepSchema = z.union([RoutineActionStepSchema, RoutineBranchStepSchema]);
export type RoutineStepDef = z.infer<typeof RoutineStepSchema>;
export type RoutineStepType = RoutineStepDef['type'];

// ───────────── Exit conditions, limits ─────────────

export const EXIT_CONDITIONS = ['opt_out', 'disqualified', 'booked', 'callback_requested', 'human_takeover', 'routine_paused', 'conversation', 'human_stage_change'] as const;
export type ExitCondition = (typeof EXIT_CONDITIONS)[number];

export const ExitConditionsSchema = z.object({
  opt_out: z.boolean().default(true),
  disqualified: z.boolean().default(true),
  booked: z.boolean().default(true),
  callback_requested: z.boolean().default(true),
  human_takeover: z.boolean().default(true),
  routine_paused: z.boolean().default(true),
  /** Spec §15.3: a real bilateral exchange (reply on any channel or answered call) after the cadence started. */
  conversation: z.boolean().default(false),
  /** Spec §15.3 "cambia el estado": a person (LIA team or CRM) moved the opportunity's stage after the cadence started. */
  human_stage_change: z.boolean().default(false),
});
export type ExitConditions = z.infer<typeof ExitConditionsSchema>;

export const RoutineDefinitionSchema = z.object({
  channels: z.array(ChannelKindSchema).max(2).default([]),
  trigger: RoutineTriggerSchema,
  eligibility: z.array(RoutineConditionSchema).max(20).default([]),
  steps: z.array(RoutineStepSchema).max(30).default([]),
  exitConditions: ExitConditionsSchema.default({
    opt_out: true,
    disqualified: true,
    booked: true,
    callback_requested: true,
    human_takeover: true,
    routine_paused: true,
    conversation: false,
    human_stage_change: false,
  }),
  /** Narrows the effective operating schedule (interpreted in its timezone). Can never widen it. */
  hoursOverride: WeeklyHoursSchema.nullable().default(null),
  /** Max dispatch attempts for a failed channel action (uncertain results are never retried). */
  maxAttempts: z.number().int().min(1).max(5).default(1),
  /** Max non-wait actions per enrollment. */
  budget: z.number().int().min(1).max(50).default(10),
  /** Deferred outreach older than this (since enrollment) is skipped with a reason. */
  maxOutreachAgeHours: z.number().int().min(1).max(24 * 30).default(72),
});
export type RoutineDefinition = z.infer<typeof RoutineDefinitionSchema>;
export type RoutineDefinitionInput = z.input<typeof RoutineDefinitionSchema>;

// ───────────── Templates ─────────────

export const ROUTINE_TEMPLATE_KEYS = [
  'call_first',
  'no_show_recovery',
  'cancellation_followup',
  'qualified_followup',
  'whatsapp_first',
  'appointment_reminder',
  'reactivation',
] as const;
export const RoutineTemplateKeySchema = z.enum(ROUTINE_TEMPLATE_KEYS);
export type RoutineTemplateKey = z.infer<typeof RoutineTemplateKeySchema>;

export const RoutineTemplateSchema = z.object({
  key: RoutineTemplateKeySchema,
  name: z.string(),
  summary: z.string(),
  /** Setter cadence from the spec (§03.1, §03.2, §06.6, §15.3) with its spec reference; null = general template. */
  specRef: z.string().nullable().default(null),
  definition: RoutineDefinitionSchema,
});
export type RoutineTemplate = z.infer<typeof RoutineTemplateSchema>;

// ───────────── DTOs ─────────────

export const EnrollmentCountsSchema = z.object({
  active: z.number().int(),
  paused: z.number().int(),
  completed: z.number().int(),
  exited: z.number().int(),
});

export const RoutineSummarySchema = z.object({
  id: Id,
  name: z.string(),
  state: RoutineStateSchema,
  projectIds: z.array(Id),
  currentVersion: z.number().int(),
  triggerType: z.enum(ROUTINE_TRIGGER_TYPES),
  channels: z.array(ChannelKindSchema),
  stepCount: z.number().int(),
  counts: EnrollmentCountsSchema,
  lastRunAt: Iso.nullable(),
  updatedAt: Iso,
});
export type RoutineSummary = z.infer<typeof RoutineSummarySchema>;

export const ActivationCheckSchema = z.object({
  /** Unmet prerequisites to go live (empty = can go live). */
  live: z.array(z.string()),
  /** Unmet prerequisites for test state. */
  test: z.array(z.string()),
});
export type ActivationCheck = z.infer<typeof ActivationCheckSchema>;

export const RoutineDetailSchema = RoutineSummarySchema.extend({
  definition: RoutineDefinitionSchema,
  versions: z.array(z.object({ version: z.number().int(), createdAt: Iso, enrollments: z.number().int() })),
  activation: ActivationCheckSchema,
  /** Channel readiness: null = usable; otherwise the honest reason it cannot send. */
  channelStatus: z.object({ whatsapp: z.string().nullable(), voice: z.string().nullable() }),
});
export type RoutineDetail = z.infer<typeof RoutineDetailSchema>;

export const RoutineListSchema = z.object({
  items: z.array(RoutineSummarySchema),
  /** Live/test routines are running (false after "Pausar todas"). */
  anyRunning: z.boolean(),
});
export type RoutineList = z.infer<typeof RoutineListSchema>;

export const CreateRoutineSchema = z.object({
  name: z.string().trim().min(1).max(120),
  projectIds: z.array(Id).max(50).default([]),
  templateKey: RoutineTemplateKeySchema.optional(),
  definition: RoutineDefinitionSchema.optional(),
});
export type CreateRoutine = z.input<typeof CreateRoutineSchema>;

export const UpdateRoutineSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  projectIds: z.array(Id).max(50).optional(),
  definition: RoutineDefinitionSchema.optional(),
});
export type UpdateRoutine = z.input<typeof UpdateRoutineSchema>;

export const ChangeRoutineStateSchema = z.object({ state: RoutineStateSchema });

export const ENROLLMENT_STATUSES = ['active', 'paused', 'completed', 'exited'] as const;
export const EnrollmentStatusSchema = z.enum(ENROLLMENT_STATUSES);
export type EnrollmentStatus = z.infer<typeof EnrollmentStatusSchema>;

/** `suspended` = held by the AI kill switch (setter §10.8) until a controlled resume. */
export const STEP_STATUSES = ['scheduled', 'running', 'done', 'skipped', 'failed', 'suspended'] as const;
export const StepStatusSchema = z.enum(STEP_STATUSES);
export type StepStatus = z.infer<typeof StepStatusSchema>;

export const EnrollmentSchema = z.object({
  id: Id,
  routineId: Id,
  routineName: z.string(),
  routineVersion: z.number().int(),
  contactId: Id,
  contactName: z.string().nullable(),
  isTestContact: z.boolean(),
  leadInterestId: Id.nullable(),
  projectName: z.string().nullable(),
  status: EnrollmentStatusSchema,
  exitReason: z.string().nullable(),
  /** Spanish label of the exit reason. */
  exitReasonLabel: z.string().nullable(),
  nextDueAt: Iso.nullable(),
  createdAt: Iso,
  updatedAt: Iso,
});
export type Enrollment = z.infer<typeof EnrollmentSchema>;

export const EnrollmentListQuerySchema = z.object({
  status: EnrollmentStatusSchema.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

export const EnrollmentListPageSchema = z.object({ items: z.array(EnrollmentSchema), nextCursor: z.string().nullable() });

export const StepExecutionSchema = z.object({
  id: Id,
  stepIndex: z.number().int(),
  stepId: z.string().nullable(),
  action: z.string(),
  title: z.string(),
  status: StepStatusSchema,
  /** Why it was skipped / failed — or, while scheduled, why it was deferred. */
  reason: z.string().nullable(),
  dueAt: Iso.nullable(),
  startedAt: Iso.nullable(),
  finishedAt: Iso.nullable(),
  intent: z
    .object({ id: Id, kind: z.string(), status: z.string(), skipReason: z.string().nullable(), idempotencyKey: z.string() })
    .nullable(),
});
export type StepExecution = z.infer<typeof StepExecutionSchema>;

export const EnrollmentDetailSchema = EnrollmentSchema.extend({ steps: z.array(StepExecutionSchema) });
export type EnrollmentDetail = z.infer<typeof EnrollmentDetailSchema>;

export const ManualEnrollSchema = z.object({ leadInterestId: Id });
export const ManualEnrollResultSchema = z.object({
  enrolled: z.boolean(),
  /** Why no enrollment was created (ineligible, duplicate, collapsed…). */
  reason: z.string().nullable(),
  enrollment: EnrollmentSchema.nullable(),
});
export type ManualEnrollResult = z.infer<typeof ManualEnrollResultSchema>;

export const PauseAllResultSchema = z.object({ pausedRoutines: z.number().int(), stoppedEnrollments: z.number().int(), skippedSteps: z.number().int() });

// ───────────── Dry run ─────────────

export const DryRunSampleSchema = z.object({
  projectId: Id.nullable().default(null),
  stage: z.string().max(80).default('assigned'),
  sourceSystem: z.string().max(60).nullable().default(null),
  isTest: z.boolean().default(true),
  whatsappPermission: PermissionStatusSchema.default('granted'),
  voicePermission: PermissionStatusSchema.default('granted'),
});

export const DryRunRequestSchema = z.object({
  leadInterestId: Id.optional(),
  sample: DryRunSampleSchema.optional(),
  /** Unsaved definition to evaluate instead of the current version. */
  definition: RoutineDefinitionSchema.optional(),
  /** Simulated start time (ISO); defaults to now. */
  at: z.string().optional(),
});
export type DryRunRequest = z.input<typeof DryRunRequestSchema>;

export const DRY_RUN_OUTCOMES = ['would_run', 'would_defer', 'would_skip', 'would_exit', 'depends'] as const;

export const DryRunItemSchema = z.object({
  stepIndex: z.number().int(),
  stepId: z.string(),
  action: z.string(),
  title: z.string(),
  /** 0 = top level, 1 = inside a branch arm. */
  depth: z.number().int(),
  arm: z.enum(['true', 'false']).nullable(),
  plannedAt: Iso.nullable(),
  outcome: z.enum(DRY_RUN_OUTCOMES),
  reason: z.string().nullable(),
});
export type DryRunItem = z.infer<typeof DryRunItemSchema>;

export const DryRunResultSchema = z.object({
  startAt: Iso,
  eligible: z.boolean(),
  eligibility: z.array(z.object({ condition: z.string(), passed: z.boolean(), detail: z.string().nullable() })),
  /** Test routine vs. live enrollment rules. */
  enrollmentNote: z.string().nullable(),
  exit: z.string().nullable(),
  items: z.array(DryRunItemSchema),
  timezone: z.string().nullable(),
});
export type DryRunResult = z.infer<typeof DryRunResultSchema>;

// ───────────── Labels (es-MX) ─────────────

export const ROUTINE_TRIGGER_LABELS: Record<RoutineTriggerType, string> = {
  lead_created: 'Prospecto nuevo',
  lead_stage_changed: 'Cambio de etapa',
  whatsapp_inbound: 'Mensaje de WhatsApp recibido',
  call_outcome: 'Resultado de llamada',
  appointment_changed: 'Cita creada o modificada',
  appointment_outcome: 'Resultado de cita informado',
  no_reply: 'Sin respuesta por un tiempo',
  manual: 'Inscripción manual',
  external_event: 'Evento externo',
};

export const ROUTINE_STEP_LABELS: Record<RoutineStepType, string> = {
  whatsapp_template: 'Plantilla de WhatsApp',
  whatsapp_text: 'Mensaje de WhatsApp',
  whatsapp_media: 'Archivo por WhatsApp',
  ai_call: 'Llamada de la IA',
  wait: 'Esperar',
  wait_until_day: 'Esperar al día de la cadencia',
  update_stage: 'Actualizar etapa',
  notify_advisor: 'Avisar a un asesor',
  create_task: 'Crear tarea',
  send_webhook: 'Enviar webhook',
  branch: 'Condición',
};

export const BRANCH_CONDITION_LABELS: Record<BranchCondition, { question: string; yes: string; no: string }> = {
  answered: { question: '¿Contestó la llamada?', yes: 'Contestó', no: 'No contestó' },
  replied: { question: '¿Respondió?', yes: 'Respondió', no: 'No respondió' },
  booked: { question: '¿Agendó una cita?', yes: 'Agendó', no: 'No agendó' },
  eligible: { question: '¿Cumple las condiciones?', yes: 'Cumple', no: 'No cumple' },
};

export const EXIT_CONDITION_LABELS: Record<ExitCondition, string> = {
  opt_out: 'Se dio de baja',
  disqualified: 'Fue descalificado',
  booked: 'Agendó una cita',
  callback_requested: 'Pidió que lo contacten después',
  human_takeover: 'Un asesor tomó la conversación',
  routine_paused: 'La rutina se pausó',
  conversation: 'Se logró conversación',
  human_stage_change: 'Una persona cambió la etapa',
};

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: 'Activa',
  paused: 'Pausada',
  completed: 'Completada',
  exited: 'Salió',
};

export const STEP_STATUS_LABELS: Record<StepStatus, string> = {
  scheduled: 'Programado',
  running: 'En curso',
  done: 'Hecho',
  skipped: 'Omitido',
  failed: 'Falló',
  suspended: 'Suspendido por contingencia',
};

export const ROUTINE_CONDITION_LABELS: Record<RoutineCondition['type'], string> = {
  project_in: 'Proyecto es',
  stage_in: 'Etapa es',
  source_system_equals: 'Origen es',
  permission_granted: 'Tiene permiso de canal',
  not_test_contact: 'No es contacto de prueba',
};
